/**
 * Tenant-independent country metadata.
 * Provides flag URLs, display names, Cloudflare country codes, and localized UI text
 * for use by GeoIP bar, country selector, and other geographic components.
 *
 * This data does NOT come from the Experience API — it is static, tenant-independent
 * presentation data (flags, names, button labels).
 */

export interface CountryMeta {
  flagUrl: string
  cloudflareCountryCode: string
  displayName: string
  closeButtonText: string
  selectCountryText: string
  continueText?: string
}

const FLAG_BASE_URL = 'https://cms.cloudinary.vpsvc.com/image/upload/c_scale,dpr_auto,f_auto,q_auto,w_46/country_flags'

export const COUNTRY_METADATA: Record<string, CountryMeta> = {
  US: {
    flagUrl: `${FLAG_BASE_URL}/usflag.svg`,
    cloudflareCountryCode: 'US',
    displayName: 'United States',
    closeButtonText: 'Close',
    selectCountryText: 'Select Country',
    continueText: 'Continue',
  },
  AU: {
    flagUrl: `${FLAG_BASE_URL}/auflag.svg`,
    cloudflareCountryCode: 'AU',
    displayName: 'Australia',
    closeButtonText: 'Close',
    selectCountryText: 'Select Country',
    continueText: 'Continue',
  },
  BE: {
    flagUrl: `${FLAG_BASE_URL}/beflag.svg`,
    cloudflareCountryCode: 'BE',
    displayName: 'België',
    closeButtonText: 'Sluiten',
    selectCountryText: 'Selecteer land',
  },
  BR: {
    flagUrl: `${FLAG_BASE_URL}/brflag.svg`,
    cloudflareCountryCode: 'BR',
    displayName: 'Brazil',
    closeButtonText: 'Fechar',
    selectCountryText: 'Select Country',
  },
  CA: {
    flagUrl: `${FLAG_BASE_URL}/caflag.svg`,
    cloudflareCountryCode: 'CA',
    displayName: 'Canada',
    closeButtonText: 'Close',
    selectCountryText: 'Select Country',
    continueText: 'Continue',
  },
  DK: {
    flagUrl: `${FLAG_BASE_URL}/dkflag.svg`,
    cloudflareCountryCode: 'DK',
    displayName: 'Danmark',
    closeButtonText: 'Luk',
    selectCountryText: 'Vælg land',
    continueText: 'Fortsæt',
  },
  DE: {
    flagUrl: `${FLAG_BASE_URL}/deflag.svg`,
    cloudflareCountryCode: 'DE',
    displayName: 'Deutschland',
    closeButtonText: 'Schließen',
    selectCountryText: 'Land auswählen',
    continueText: 'Weiter',
  },
  ES: {
    flagUrl: `${FLAG_BASE_URL}/esflag.svg`,
    cloudflareCountryCode: 'ES',
    displayName: 'España',
    closeButtonText: 'Cerrar',
    selectCountryText: 'Seleccionar un país',
    continueText: 'Continuar',
  },
  FR: {
    flagUrl: `${FLAG_BASE_URL}/frflag.svg`,
    cloudflareCountryCode: 'FR',
    displayName: 'France',
    closeButtonText: 'Fermer',
    selectCountryText: 'Sélectionner pays',
    continueText: 'Continuer',
  },
  IN: {
    flagUrl: `${FLAG_BASE_URL}/inflag.svg`,
    cloudflareCountryCode: 'IN',
    displayName: 'India',
    closeButtonText: 'Close',
    selectCountryText: 'Select Country',
    continueText: 'Continue',
  },
  IE: {
    flagUrl: `${FLAG_BASE_URL}/ieflag.svg`,
    cloudflareCountryCode: 'IE',
    displayName: 'Ireland',
    closeButtonText: 'Close',
    selectCountryText: 'Select Country',
    continueText: 'Continue',
  },
  IT: {
    flagUrl: `${FLAG_BASE_URL}/itFlag.svg`,
    cloudflareCountryCode: 'IT',
    displayName: 'Italia',
    closeButtonText: 'Chiudi',
    selectCountryText: 'Seleziona Paese',
    continueText: 'Continua',
  },
  NL: {
    flagUrl: `${FLAG_BASE_URL}/nlflag.svg`,
    cloudflareCountryCode: 'NL',
    displayName: 'Nederland',
    closeButtonText: 'Sluiten',
    selectCountryText: 'Selecteer land',
    continueText: 'Doorgaan',
  },
  NZ: {
    flagUrl: `${FLAG_BASE_URL}/nzflag.svg`,
    cloudflareCountryCode: 'NZ',
    displayName: 'New Zealand',
    closeButtonText: 'Close',
    selectCountryText: 'Select Country',
    continueText: 'Continue',
  },
  NO: {
    flagUrl: `${FLAG_BASE_URL}/noflag.svg`,
    cloudflareCountryCode: 'NO',
    displayName: 'Norge',
    closeButtonText: 'Lukk',
    selectCountryText: 'Velg land',
    continueText: 'Fortsett',
  },
  AT: {
    flagUrl: `${FLAG_BASE_URL}/atflag.svg`,
    cloudflareCountryCode: 'AT',
    displayName: 'Österreich',
    closeButtonText: 'Schließen',
    selectCountryText: 'Land auswählen',
    continueText: 'Weiter',
  },
  PT: {
    flagUrl: `${FLAG_BASE_URL}/ptflag.svg`,
    cloudflareCountryCode: 'PT',
    displayName: 'Portugal',
    closeButtonText: 'Fechar',
    selectCountryText: 'Selecionar país',
    continueText: 'Continuar',
  },
  CH: {
    flagUrl: `${FLAG_BASE_URL}/chflag.svg`,
    cloudflareCountryCode: 'CH',
    displayName: 'Schweiz',
    closeButtonText: 'Schließen',
    selectCountryText: 'Land auswählen',
  },
  SG: {
    flagUrl: `${FLAG_BASE_URL}/sgflag.svg`,
    cloudflareCountryCode: 'SG',
    displayName: 'Singapore',
    closeButtonText: 'Close',
    selectCountryText: 'Select Country',
    continueText: 'Continue',
  },
  FI: {
    flagUrl: `${FLAG_BASE_URL}/fiflag.svg`,
    cloudflareCountryCode: 'FI',
    displayName: 'Suomi',
    closeButtonText: 'Sulje',
    selectCountryText: 'Valitse maa',
    continueText: 'Jatka',
  },
  SE: {
    flagUrl: `${FLAG_BASE_URL}/seflag.svg`,
    cloudflareCountryCode: 'SE',
    displayName: 'Sverige',
    closeButtonText: 'Stäng',
    selectCountryText: 'Välj land',
    continueText: 'Fortsätt',
  },
  GB: {
    flagUrl: `${FLAG_BASE_URL}/gbflag.svg`,
    cloudflareCountryCode: 'GB',
    displayName: 'United Kingdom',
    closeButtonText: 'Close',
    selectCountryText: 'Select Country',
    continueText: 'Continue',
  },
}

/**
 * Get country metadata by uppercase country code.
 * Returns undefined if the country code is not in the metadata table.
 */
export function getCountryMeta(countryCode: string): CountryMeta | undefined {
  return COUNTRY_METADATA[countryCode.toUpperCase()]
}
